import { Button, Card, Form, Input, Modal, Popconfirm, Spin, Table, notification } from "antd";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { editMerchant, getMerchantById } from "../util/APIUtils";
import AddressAutoComplete from "./AddressAutoComplete";

const EMPTY_FORM = { street: "", unit: "", city: "", province: "", postalCode: "", country: "" };

const TIMESLOT_REGEX = /^(0[0-9]|1[0-9]|2[0-3])-(0[0-9]|1[0-9]|2[0-3])$/;

const validateTimeslot = (_, value) => {
  if (!value) return Promise.resolve();
  if (!TIMESLOT_REGEX.test(value)) {
    return Promise.reject(new Error("Must be HH-HH in 24-hour format (e.g. 07-10)"));
  }
  const [start, end] = value.split("-").map(Number);
  if (end <= start) {
    return Promise.reject(new Error("End time must be after start time"));
  }
  return Promise.resolve();
};

const B2bAddresses = () => {
  const { merchantId } = useParams();
  const [merchantName, setMerchantName] = useState("");
  const [addresses, setAddresses] = useState([]);
  const [pickupTimeslot, setPickupTimeslot] = useState("");
  const [isEditingTimeslot, setIsEditingTimeslot] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [timeslotForm] = Form.useForm();

  useEffect(() => {
    getMerchantById(merchantId)
      .then((merchant) => {
        setMerchantName(merchant.name);
        setAddresses(merchant.b2bApprovedDestinationAddresses || []);
        setPickupTimeslot(merchant.b2bPickupTimeslot || "");
        timeslotForm.setFieldsValue({ b2bPickupTimeslot: merchant.b2bPickupTimeslot || "" });
        setIsLoading(false);
      })
      .catch((e) => {
        notification.open({
          message: "Error!",
          description: `Could not get merchant. ${e?.message || ""}`,
          duration: 3,
          type: "error",
          placement: "topRight",
        });
        setIsLoading(false);
      });
  }, [merchantId]);

  const saveAddresses = async (updatedAddresses) => {
    setIsLoading(true);
    try {
      await editMerchant({ b2bApprovedDestinationAddresses: updatedAddresses }, merchantId);
      setAddresses(updatedAddresses);
      setIsLoading(false);
      return true;
    } catch (e) {
      notification.open({
        message: "Error!",
        description: `Could not update addresses. ${e?.message || ""}`,
        duration: 3,
        type: "error",
        placement: "topRight",
      });
      setIsLoading(false);
      return false;
    }
  };

  const savePickupTimeslot = async () => {
    try {
      const values = await timeslotForm.validateFields();
      setIsLoading(true);
      await editMerchant({ b2bPickupTimeslot: values.b2bPickupTimeslot || undefined }, merchantId);
      setPickupTimeslot(values.b2bPickupTimeslot);
      setIsEditingTimeslot(false);
      notification.open({
        message: "Success!",
        description: `Pickup timeslot set to "${values.b2bPickupTimeslot}"`,
        duration: 3,
        type: "success",
        placement: "topRight",
      });
    } catch (e) {
      if (e?.errorFields) return;
      notification.open({
        message: "Error!",
        description: `Could not update pickup timeslot. ${e?.message || ""}`,
        duration: 3,
        type: "error",
        placement: "topRight",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onEditTimeslot = () => {
    timeslotForm.setFieldsValue({ b2bPickupTimeslot: pickupTimeslot });
    setIsEditingTimeslot(true);
  };

  const onCancelEditTimeslot = () => {
    timeslotForm.resetFields();
    setIsEditingTimeslot(false);
  };

  const onAddAddress = async () => {
    try {
      const values = await form.validateFields();
      const newAddress = { ...values, number: " " };
      setIsModalOpen(false);
      const updatedAddresses = [...addresses, newAddress];
      const success = await saveAddresses(updatedAddresses);
      if (success) {
        form.resetFields();
        notification.open({
          message: "Success!",
          description: `Added "${values.street}, ${values.city}" to approved B2B addresses.`,
          duration: 3,
          type: "success",
          placement: "topRight",
        });
      }
    } catch (e) {
      if (e?.errorFields) return;
    }
  };

  const onRemoveAddress = async (index) => {
    const removed = addresses[index];
    const updatedAddresses = addresses.filter((_, i) => i !== index);
    const success = await saveAddresses(updatedAddresses);
    if (success) {
      notification.open({
        message: "Success!",
        description: `Removed "${[removed.street].filter(Boolean).join(" ")}, ${
          removed.city
        }" from approved B2B addresses.`,
        duration: 3,
        type: "success",
        placement: "topRight",
      });
    }
  };

  const formatAddress = (record) => {
    const parts = [record.street, record.unit, record.city, record.province, record.postalCode, record.country].filter(
      Boolean,
    );
    return parts.join(", ");
  };

  const columns = [
    {
      title: "Street",
      dataIndex: "street",
      key: "street",
    },
    {
      title: "Unit",
      dataIndex: "unit",
      key: "unit",
      render: (text) => text || "-",
    },
    {
      title: "City",
      dataIndex: "city",
      key: "city",
    },
    {
      title: "Province",
      dataIndex: "province",
      key: "province",
    },
    {
      title: "Postal Code",
      dataIndex: "postalCode",
      key: "postalCode",
    },
    {
      title: "Country",
      dataIndex: "country",
      key: "country",
    },
    {
      title: "",
      key: "actions",
      width: "10%",
      render: (_, record, index) => (
        <Popconfirm
          title={`Remove "${formatAddress(record)}"?`}
          onConfirm={() => onRemoveAddress(index)}
          okText="Yes"
          cancelText="No"
        >
          <Button type="link" danger disabled={isLoading}>
            Remove
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const dataSource = addresses.map((address, index) => ({
    key: index,
    ...address,
  }));

  return (
    <Spin spinning={isLoading}>
      <div className="viewMerchants-container">
        <Card
          title={`B2B Approved Destination Addresses — ${merchantName}`}
          extra={
            <Button
              type="primary"
              onClick={() => {
                setIsModalOpen(true);
              }}
            >
              Add Address
            </Button>
          }
        >
          <Table
            size="small"
            pagination={false}
            columns={columns}
            dataSource={dataSource}
            rowKey="key"
            locale={{ emptyText: "No approved B2B addresses configured" }}
          />
        </Card>

        <Card title="B2B Pickup Timeslot" style={{ marginTop: 16 }}>
          {pickupTimeslot && !isEditingTimeslot ? (
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span>{pickupTimeslot}</span>
              <Button type="link" onClick={onEditTimeslot} disabled={isLoading}>
                Edit
              </Button>
            </div>
          ) : (
            <Form form={timeslotForm} layout="inline" initialValues={{ b2bPickupTimeslot: pickupTimeslot }}>
              <Form.Item
                name="b2bPickupTimeslot"
                rules={[{ required: true, message: "Timeslot is required" }, { validator: validateTimeslot }]}
              >
                <Input placeholder="e.g. 07-10" style={{ width: 160 }} />
              </Form.Item>
              <Form.Item>
                <Button type="primary" onClick={savePickupTimeslot} disabled={isLoading}>
                  Save
                </Button>
              </Form.Item>
              {isEditingTimeslot && (
                <Form.Item>
                  <Button onClick={onCancelEditTimeslot} disabled={isLoading}>
                    Cancel
                  </Button>
                </Form.Item>
              )}
            </Form>
          )}
        </Card>

        <Modal
          title="Add B2B Approved Address"
          visible={isModalOpen}
          destroyOnClose
          onOk={onAddAddress}
          onCancel={() => {
            setIsModalOpen(false);
            form.resetFields();
          }}
          okText="Add"
        >
          <Form form={form} layout="vertical" initialValues={EMPTY_FORM}>
            <Form.Item label="Search Address">
              <AddressAutoComplete
                onAddressSelect={({ street, city, province, postalCode, country }) => {
                  form.setFieldsValue({ street, city, province, postalCode, country });
                }}
              />
            </Form.Item>
            <Form.Item name="street" label="Street" rules={[{ required: true, message: "Street is required" }]}>
              <Input placeholder="e.g. 150 John St" />
            </Form.Item>
            <Form.Item name="unit" label="Unit">
              <Input placeholder="e.g. Suite 200" />
            </Form.Item>
            <Form.Item name="city" label="City" rules={[{ required: true, message: "City is required" }]}>
              <Input placeholder="e.g. Toronto" />
            </Form.Item>
            <Form.Item name="province" label="Province" rules={[{ required: true, message: "Province is required" }]}>
              <Input placeholder="e.g. ON" />
            </Form.Item>
            <Form.Item
              name="postalCode"
              label="Postal Code"
              rules={[{ required: true, message: "Postal code is required" }]}
            >
              <Input placeholder="e.g. M5V 3E2" />
            </Form.Item>
            <Form.Item name="country" label="Country" rules={[{ required: true, message: "Country is required" }]}>
              <Input placeholder="e.g. CA" />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </Spin>
  );
};

export default B2bAddresses;
